import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { useRef, useState } from "react";
import { Image01Icon, ViewIcon } from "hugeicons-react";
import SectionBadge from "../ui/SectionBadge";

const luxuryCars = [
  {
    id: 1,
    name: "بورش 911 GT3",
    image: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=1200&auto=format&fit=crop",
    style: "rounded-tl-[80px] rounded-br-[80px] md:col-span-2",
    parallax: [0, -40],
    accentColor: "rgba(220, 20, 60, 0.4)",
    specs: { "اللمعان": "98%", "الحماية": "5 سنوات", "الصلابة": "9H+" }
  },
  {
    id: 2,
    name: "لامبورغيني أوروس",
    image: "https://images.unsplash.com/photo-1544636331-e26879cd4d9b?q=80&w=1200&auto=format&fit=crop",
    style: "rounded-tr-[120px] rounded-bl-[40px]",
    parallax: [0, 40],
    accentColor: "rgba(255, 165, 0, 0.4)",
    specs: { "اللمعان": "99%", "الحماية": "7 سنوات", "الصلابة": "10H" }
  },
  {
    id: 3,
    name: "مرسيدس جي كلاس",
    image: "https://images.unsplash.com/photo-1520050206274-a1af446338cb?q=80&w=1200&auto=format&fit=crop",
    style: "rounded-tr-[40px] rounded-bl-[120px]",
    parallax: [0, -30],
    accentColor: "rgba(255, 255, 255, 0.2)",
    specs: { "اللمعان": "95%", "الحماية": "3 سنوات", "الصلابة": "9H" }
  },
  {
    id: 4,
    name: "فيراري F8 تريبيوت",
    image: "https://images.unsplash.com/photo-1592198084033-aade902d1aae?q=80&w=1200&auto=format&fit=crop",
    style: "rounded-tl-[80px] rounded-br-[80px] md:col-span-2",
    parallax: [0, 50],
    accentColor: "rgba(220, 20, 60, 0.4)",
    specs: { "اللمعان": "100%", "الحماية": "مدى الحياة", "الصلابة": "كريستال" }
  }
];

function GalleryItem({ car }: { car: typeof luxuryCars[0] }) {
  const itemRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const cursorX = useMotionValue(0);
  const cursorY = useMotionValue(0);
  
  const mouseXSpring = useSpring(x);
  const mouseYSpring = useSpring(y);
  
  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["6deg", "-6deg"]);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-6deg", "6deg"]);

  const { scrollYProgress } = useScroll({
    target: itemRef,
    offset: ["start end", "end start"]
  });

  const parallaxY = useTransform(scrollYProgress, [0, 1], car.parallax);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.98, 1, 0.98]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!itemRef.current) return;
    const rect = itemRef.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mX = e.clientX - rect.left;
    const mY = e.clientY - rect.top;
    const xPct = mX / width - 0.5;
    const yPct = mY / height - 0.5;
    x.set(xPct);
    y.set(yPct);
    cursorX.set(e.clientX);
    cursorY.set(e.clientY);
    itemRef.current.style.setProperty("--mouse-x", `${mX}px`);
    itemRef.current.style.setProperty("--mouse-y", `${mY}px`);
  };

  return (
    <motion.div
      ref={itemRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { x.set(0); y.set(0); setIsHovered(false); }}
      style={{ y: parallaxY, scale, rotateX, rotateY, transformStyle: "preserve-3d", perspective: "1000px" }}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      className={`relative group overflow-hidden ${car.style} h-[380px] md:h-[550px] border border-white/10 cursor-none`}
    >
      <motion.img
        src={car.image}
        alt={car.name}
        className="absolute inset-0 w-full h-full object-cover filter brightness-[0.4] group-hover:brightness-90 transition-all duration-1000"
        style={{ transform: "translateZ(-50px) scale(1.1)" }}
      />

      {/* Floating HUD Labels */}
      <div className="absolute top-8 left-8 flex flex-col gap-2 z-30 opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200" style={{ transform: "translateZ(60px)" }}>
          {Object.entries(car.specs).map(([key, val]) => (
            <div key={key} className="flex items-center gap-3 px-3 py-1 bg-black/40 backdrop-blur-md border border-white/10 rounded-lg">
               <span className="text-[8px] font-black text-white/40 uppercase tracking-widest">{key}</span>
               <span className="text-[10px] font-black text-primary">{val}</span>
            </div>
          ))}
      </div>

      <motion.div 
        className="absolute inset-0 z-20 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: `radial-gradient(600px circle at var(--mouse-x, 0) var(--mouse-y, 0), ${car.accentColor}, transparent 80%)` }}
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent opacity-90 group-hover:opacity-50 transition-opacity z-10" />

      <div className="absolute inset-x-0 bottom-0 z-30 flex flex-col justify-end p-8 md:p-14" style={{ transform: "translateZ(100px)" }}>
        {/* Wet Look Overlay */}
        <div className="absolute inset-0 z-0 pointer-events-none opacity-0 group-hover:opacity-30 transition-opacity bg-gradient-to-br from-white/10 to-transparent" />
        
        <div className="flex items-center gap-4 mb-6 transition-all duration-700 transform group-hover:-translate-y-4 relative z-10" dir="rtl">
           <div className="h-[2px] w-12 bg-primary shadow-[0_0_15px_rgba(220,10,50,0.8)]" />
           <span className="text-primary text-[10px] md:text-sm font-black uppercase tracking-[0.5em] drop-shadow-neon">عناية استثنائية</span>
        </div>
        
        <h3 className="text-4xl md:text-7xl font-black text-white text-right leading-none mb-10 transform group-hover:-translate-x-4 transition-transform duration-700">
          {car.name.split(' ').map((word, i) => (
            <span key={i} className={i === 1 ? "text-primary italic" : ""}>{word} </span>
          ))}
        </h3>
        
        <div className="flex justify-end opacity-0 group-hover:opacity-100 translate-y-12 group-hover:translate-y-0 transition-all duration-700 delay-150">
           <button className="relative group/btn flex items-center gap-3 px-8 py-4 bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl text-white font-black overflow-hidden transition-all hover:border-primary">
              <span className="relative z-10 text-base">مشاهدة التحول</span>
              <ViewIcon size={20} className="relative z-10 group-hover/btn:rotate-12 transition-transform" />
              <div className="absolute inset-0 bg-primary translate-y-full group-hover/btn:translate-y-0 transition-transform duration-500" />
           </button>
        </div>
      </div>

      <motion.div 
        className="fixed w-20 h-20 border border-primary/30 rounded-full z-[100] pointer-events-none hidden md:flex items-center justify-center backdrop-blur-[2px] mix-blend-difference opacity-0 group-hover:opacity-100 transition-all"
        style={{ x: cursorX, y: cursorY, translateX: "-50%", translateY: "-50%", display: isHovered ? 'flex' : 'none' }}
      >
        <div className="w-2 h-2 bg-primary rounded-full shadow-[0_0_15px_rgba(220,10,50,1)]" />
      </motion.div>
    </motion.div>
  );
}

export default function LuxuryGallery() {
  return (
    <section className="py-24 md:py-32 relative overflow-hidden bg-[#020000]">
      {/* Background Atmosphere */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
        <div className="absolute inset-0 opacity-[0.03] bg-carbon z-0" />
        
        {/* Subtle Background Glows */}
        <div className="absolute top-1/4 -right-1/4 w-[800px] h-[800px] bg-primary/5 blur-[160px] rounded-full" />
        <div className="absolute bottom-1/4 -left-1/4 w-[800px] h-[800px] bg-red-900/5 blur-[160px] rounded-full" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-24" dir="rtl">
          <motion.div initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="flex flex-col items-center gap-6">
            <SectionBadge text="معرض الإبداع" icon={Image01Icon} />
            <h2 className="text-5xl md:text-8xl font-black text-white tracking-tighter leading-none mb-10 select-none opacity-90">
              تحف <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-red-500 to-white italic px-4">حقيقية</span>
            </h2>
            <div className="max-w-4xl relative">
              <p className="text-gray-400 text-lg md:text-2xl leading-relaxed font-bold px-12 border-l-2 border-primary/20">
                نحن لا نوفر خدمة، نحن نصنع تجربة بصرية باذخة. استثمر في مظهر سيارتك كما تستثمر في محركها.
              </p>
            </div>
          </motion.div>
        </div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14">
          {luxuryCars.map((car) => (
            <GalleryItem key={car.id} car={car} />
          ))}
        </div>

        {/* Final Premium Signature Mark */}
        <div className="mt-32 flex flex-col items-center opacity-30 hover:opacity-100 transition-all duration-1000 group">
           <div className="h-px w-32 bg-gradient-to-r from-transparent via-primary to-transparent mb-10 group-hover:w-48 transition-all duration-700" />
           <p className="font-serif italic text-3xl text-white select-none tracking-widest">تميز M Four</p>
           <p className="text-[10px] font-black text-primary uppercase tracking-[0.5em] mt-3">مختبرات العناية الاحترافية المعتمدة</p>
        </div>
      </div>
    </section>
  );
}
